import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { TagService } from '../../services/TagService';
import { Tag, CreateTagData, UpdateTagData } from '../types/tag';
import { translationEnabledAtom } from './translationAtoms';

// 标签列表状态
export const tagsAtom = atomWithStorage<Tag[]>('tags', []);

// 标签加载状态
export const tagsLoadingAtom = atom(false);
export const tagsErrorAtom = atom<string | null>(null);

// 获取标签列表的操作原子
export const fetchTagsAtom = atom(
  null,
  async (get, set) => {
    try {
      set(tagsLoadingAtom, true);
      set(tagsErrorAtom, null);
      
      const tags = await TagService.fetchTags();
      set(tagsAtom, tags);
      
      return true;
    } catch (error) {
      console.error('获取标签列表失败:', error);
      set(tagsErrorAtom, error instanceof Error ? error.message : '获取标签列表失败');
      return false;
    } finally {
      set(tagsLoadingAtom, false);
    }
  }
);

// 创建标签操作原子（使用乐观更新）
export const createTagAtom = atom(
  null,
  async (get, set, tagData: CreateTagData) => {
    // 检查是否在翻译模式中
    if (get(translationEnabledAtom)) {
      alert('翻译模式下不能编辑内容');
      return false;
    }

    try {
      // 生成临时ID用于乐观更新
      const tempId = crypto.randomUUID();
      
      // 创建乐观更新的临时标签
      const tempTag: Tag = {
        id: tagData.id || tempId,
        userId: '', // 会被后端填充
        title: tagData.title,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      // 乐观更新：立即更新UI状态
      set(tagsAtom, (prev) => [...prev, tempTag]);
      
      // 调用API创建标签
      const createdTag = await TagService.createTag({
        ...tagData,
        id: tempTag.id, // 使用临时ID，确保一致性
      });
      
      // 更新状态中的标签为后端返回的正确数据
      set(tagsAtom, (prev) => 
        prev.map((tag) => (tag.id === tempTag.id ? createdTag : tag))
      );
      
      return true;
    } catch (error) {
      console.error('创建标签失败:', error);
      
      // 失败时回滚乐观更新
      set(tagsAtom, (prev) => 
        prev.filter((tag) => !tagData.id || tag.id !== tagData.id)
      );
      
      return false;
    }
  }
);

// 更新标签操作原子（使用乐观更新）
export const updateTagAtom = atom(
  null,
  async (get, set, tagData: UpdateTagData) => {
    // 检查是否在翻译模式中
    if (get(translationEnabledAtom)) {
      alert('翻译模式下不能编辑内容');
      return false;
    }

    try {
      // 保存原始标签用于回滚
      const originalTag = get(tagsAtom).find((t) => t.id === tagData.id);
      if (!originalTag) {
        throw new Error('找不到要更新的标签');
      }
      
      // 乐观更新：立即更新UI状态
      set(tagsAtom, (prev) => 
        prev.map((tag) => (tag.id === tagData.id 
          ? { ...tag, title: tagData.title, updatedAt: new Date().toISOString() } 
          : tag
        ))
      );
      
      // 调用API更新标签
      const updatedTag = await TagService.updateTag(tagData);
      
      // 更新状态中的标签为后端返回的正确数据
      set(tagsAtom, (prev) => 
        prev.map((tag) => (tag.id === tagData.id ? updatedTag : tag))
      );
      
      return true;
    } catch (error) {
      console.error('更新标签失败:', error);
      
      // 失败时回滚乐观更新
      const originalTag = get(tagsAtom).find((t) => t.id === tagData.id);
      if (originalTag) {
        set(tagsAtom, (prev) => 
          prev.map((tag) => (tag.id === tagData.id ? originalTag : tag))
        );
      }
      
      return false;
    }
  }
);

// 删除标签操作原子（使用乐观更新）
export const deleteTagAtom = atom(
  null,
  async (get, set, tagId: string) => {
    // 检查是否在翻译模式中
    if (get(translationEnabledAtom)) {
      alert('翻译模式下不能编辑内容');
      return false;
    }

    try {
      // 保存原始标签用于回滚
      const originalTags = get(tagsAtom);
      const tagToDelete = originalTags.find((t) => t.id === tagId);
      if (!tagToDelete) {
        throw new Error('找不到要删除的标签');
      }
      
      // 乐观更新：立即更新UI状态
      set(tagsAtom, (prev) => prev.filter((tag) => tag.id !== tagId));
      
      // 调用API删除标签
      await TagService.deleteTag(tagId);
      
      return true;
    } catch (error) {
      console.error('删除标签失败:', error);
      
      // 失败时回滚乐观更新
      set(tagsAtom, get(tagsAtom));
      
      return false;
    }
  }
); 